import { useState, useRef } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ScanBarcode, Camera, TrendingUp, TrendingDown, Minus,
  AlertCircle, Loader2, ExternalLink, ShoppingCart, Plus,
  CheckCircle2, XCircle, RefreshCw,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';

type Tab = 'barcode' | 'photo';

interface SoldListing {
  title: string;
  pricePence: number;
  soldDate: string;
  condition: string;
  url: string;
}

interface SoldData {
  count: number;
  totalFound: number;
  averagePence: number;
  lowestPence: number;
  highestPence: number;
  medianPence: number;
  recentSales: SoldListing[];
  searchQuery: string;
  daysBack: number;
}

interface ProfitSide {
  feePence: number;
  netAfterFeesPence: number;
  profitPence: number | null;
  marginPct: number | null;
}

interface Profit {
  suggestedSellPence: number;
  ebay: ProfitSide;
  vinted: ProfitSide;
}

interface Identified {
  brand: string;
  model: string;
  category: string;
  keywords: string;
  confidence: 'high' | 'medium' | 'low';
}

interface PlatformFee {
  fvfPercent: number;
  fixedFeePence: number;
  notes: string;
  source: string;
}

interface LiveFees {
  ebay: PlatformFee;
  vinted: PlatformFee;
  asOf: string;
  fetchedAt: string;
}

interface ScoutResult {
  barcode?: string;
  identified?: Identified;
  soldData: SoldData | null;
  profit: Profit | null;
  fees: LiveFees | null;
  warning?: string;
}

function p(pence: number) {
  return `£${(pence / 100).toFixed(2)}`;
}

function formatDate(iso: string) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

const CONFIDENCE_COLOUR: Record<string, string> = {
  high: 'text-secondary-400',
  medium: 'text-amber-400',
  low: 'text-orange-400',
};

export function ScoutPage() {
  const [tab, setTab] = useState<Tab>('barcode');
  const [buyPrice, setBuyPrice] = useState('');
  const [shipping, setShipping] = useState('3.50');
  const [result, setResult] = useState<ScoutResult | null>(null);

  // Fetch live fees on page load — cached 24h on the backend
  const { data: liveFees, isLoading: feesLoading, refetch: refetchFees } = useQuery<LiveFees>({
    queryKey: ['scout-fees'],
    queryFn: () => api.get('/scout/fees').then((r) => r.data),
    staleTime: 1000 * 60 * 60, // treat as fresh for 1h on the frontend too
  });

  // Barcode state
  const [barcode, setBarcode] = useState('');

  // Photo state
  const [photoUrl, setPhotoUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const buyPricePence = buyPrice ? Math.round(parseFloat(buyPrice) * 100) : undefined;
  const shippingPence = Math.round((parseFloat(shipping) || 3.5) * 100);

  const barcodeLookup = useMutation({
    mutationFn: () =>
      api.post<ScoutResult>('/scout/barcode', {
        barcode,
        buyPricePence,
        shippingPence,
      }).then((r) => r.data),
    onSuccess: (data) => setResult(data),
  });

  const photoLookup = useMutation({
    mutationFn: (url: string) =>
      api.post<ScoutResult>('/scout/photo', {
        imageUrl: url,
        buyPricePence,
        shippingPence,
      }).then((r) => r.data),
    onSuccess: (data) => setResult(data),
  });

  async function uploadPhoto(file: File) {
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/uploads/photo', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPhotoUrl(data.url);
      photoLookup.mutate(data.url);
    } catch {
      // silent — error shown via mutation state
    } finally {
      setUploading(false);
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) uploadPhoto(file);
  }

  function createListing() {
    if (!result) return;
    const id = result.identified;
    const params = new URLSearchParams();
    if (id?.brand && id.brand !== 'Unknown') params.set('brand', id.brand);
    if (id?.category) params.set('category', id.category);
    if (id?.keywords) params.set('title', id.keywords);
    if (result.profit?.suggestedSellPence) params.set('price', String(result.profit.suggestedSellPence));
    if (photoUrl) params.set('photoUrl', photoUrl);
    navigate(`/dashboard/new?${params.toString()}`);
  }

  const isPending = barcodeLookup.isPending || photoLookup.isPending || uploading;
  const isError = barcodeLookup.isError || photoLookup.isError;
  const errorMsg = (barcodeLookup.error as any)?.response?.data?.error
    || (photoLookup.error as any)?.response?.data?.error
    || 'Something went wrong. Try again.';

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div>
        <h1 className="text-base font-semibold tracking-tight flex items-center gap-2">
          <ScanBarcode size={16} className="text-secondary-400" />
          Scout
        </h1>
        <p className="text-[11.5px] text-white/25 mt-0.5">
          Scan a barcode or photo to get live eBay UK sold prices before you buy to resell
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[380px_1fr] gap-4 items-start">
        {/* Left — input panel */}
        <div className="space-y-3">
          {/* Tab switcher */}
          <div className="flex rounded-lg bg-white/[0.04] p-0.5 border border-white/[0.06]">
            {(['barcode', 'photo'] as Tab[]).map((t) => (
              <button
                key={t}
                onClick={() => { setTab(t); setResult(null); }}
                className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-[12px] font-medium transition-colors ${
                  tab === t ? 'bg-primary-500/20 text-primary-300' : 'text-white/40 hover:text-white/60'
                }`}
              >
                {t === 'barcode' ? <ScanBarcode size={13} /> : <Camera size={13} />}
                {t === 'barcode' ? 'Barcode' : 'Photo'}
              </button>
            ))}
          </div>

          {/* Barcode tab */}
          {tab === 'barcode' && (
            <div className="card p-5 space-y-3">
              <p className="text-[12px] text-white/40">
                Enter the barcode number from the product. Works with UPC, EAN, and ISBN.
              </p>
              <input
                className="input font-mono tracking-widest text-center text-base"
                placeholder="e.g. 5055273027261"
                value={barcode}
                onChange={(e) => setBarcode(e.target.value.replace(/\D/g, '').slice(0, 14))}
                onKeyDown={(e) => e.key === 'Enter' && barcode.length >= 8 && barcodeLookup.mutate()}
              />
              <p className="text-[10.5px] text-white/20 text-center">
                {barcode.length > 0 ? `${barcode.length} digits` : 'UPC = 12 digits · EAN = 13 digits · ISBN = 10 or 13'}
              </p>
              <button
                onClick={() => barcodeLookup.mutate()}
                disabled={barcode.length < 8 || isPending}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {isPending ? <Loader2 size={14} className="animate-spin" /> : <ScanBarcode size={14} />}
                {isPending ? 'Looking up...' : 'Look up sold prices'}
              </button>
            </div>
          )}

          {/* Photo tab */}
          {tab === 'photo' && (
            <div className="card p-5 space-y-3">
              <p className="text-[12px] text-white/40">
                Upload a photo of the item. AI will identify it and find live eBay sold prices.
              </p>

              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />

              {photoUrl ? (
                <div className="relative rounded-lg overflow-hidden">
                  <img src={photoUrl} alt="Scanned item" className="w-full h-44 object-cover rounded-lg" />
                  <button
                    onClick={() => { setPhotoUrl(''); setResult(null); fileRef.current?.click(); }}
                    className="absolute top-2 right-2 bg-black/60 text-white text-[11px] px-2 py-1 rounded"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileRef.current?.click()}
                  disabled={isPending}
                  className="w-full h-36 border-2 border-dashed border-white/[0.1] rounded-lg flex flex-col items-center justify-center gap-2 hover:border-primary-500/40 hover:bg-primary-500/[0.03] transition-colors"
                >
                  {isPending ? (
                    <Loader2 size={22} className="text-white/30 animate-spin" />
                  ) : (
                    <Camera size={22} className="text-white/30" />
                  )}
                  <span className="text-[12px] text-white/30">
                    {isPending ? 'Analysing...' : 'Tap to upload photo'}
                  </span>
                </button>
              )}

              {photoUrl && !isPending && (
                <button
                  onClick={() => photoLookup.mutate(photoUrl)}
                  disabled={isPending}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  <Camera size={14} /> Scan this photo
                </button>
              )}
            </div>
          )}

          {/* Profit calculator inputs */}
          <div className="card p-5 space-y-3">
            <h3 className="text-[12px] font-semibold text-white/60">Profit calculator</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-white/35 font-medium block mb-1">Buy price (£)</label>
                <input
                  className="input"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={buyPrice}
                  onChange={(e) => setBuyPrice(e.target.value)}
                />
              </div>
              <div>
                <label className="text-[10px] text-white/35 font-medium block mb-1">Shipping (£)</label>
                <input
                  className="input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={shipping}
                  onChange={(e) => setShipping(e.target.value)}
                />
              </div>
            </div>

            {/* Live fee display */}
            <div className="border-t border-white/[0.05] pt-2.5 space-y-1.5">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-white/25 uppercase tracking-wider">Live seller fees</span>
                <button
                  onClick={() => refetchFees()}
                  disabled={feesLoading}
                  className="text-white/20 hover:text-white/50 transition-colors"
                  title="Refresh fees"
                >
                  <RefreshCw size={11} className={feesLoading ? 'animate-spin' : ''} />
                </button>
              </div>

              {feesLoading && (
                <p className="text-[10.5px] text-white/25">Fetching current fees...</p>
              )}

              {liveFees && !feesLoading && (
                <>
                  <FeeRow
                    platform="eBay"
                    colour="#e53935"
                    fee={liveFees.ebay}
                  />
                  <FeeRow
                    platform="Vinted"
                    colour="#09b864"
                    fee={liveFees.vinted}
                  />
                  <p className="text-[9.5px] text-white/20 pt-0.5">
                    Last checked: {liveFees.asOf || new Date(liveFees.fetchedAt).toLocaleDateString('en-GB')}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Right — results */}
        <div className="space-y-3">
          {/* Empty state */}
          {!result && !isPending && !isError && (
            <div className="card p-10 flex flex-col items-center justify-center text-center">
              <ScanBarcode size={36} className="text-white/10 mb-3" />
              <p className="text-sm text-white/30">Scan a barcode or upload a photo<br />to see live sold prices</p>
              <p className="text-[11px] text-white/20 mt-2">Powered by eBay UK completed listings</p>
            </div>
          )}

          {/* Loading */}
          {isPending && (
            <div className="card p-10 flex flex-col items-center justify-center gap-3">
              <Loader2 size={28} className="text-primary-400 animate-spin" />
              <p className="text-sm text-white/40">
                {tab === 'photo' ? 'Identifying item and fetching sold data...' : 'Fetching eBay UK sold data...'}
              </p>
            </div>
          )}

          {/* Error */}
          {isError && (
            <div className="card p-5 flex items-start gap-3">
              <AlertCircle size={16} className="text-danger mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-danger">Lookup failed</p>
                <p className="text-[11px] text-white/40 mt-0.5">{errorMsg}</p>
              </div>
            </div>
          )}

          {/* Results */}
          {result && !isPending && (
            <>
              {/* Identified item (photo only) */}
              {result.identified && (
                <div className="card p-4 flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] text-white/30 uppercase tracking-wider mb-1">Identified item</div>
                    <div className="text-sm font-semibold">
                      {result.identified.brand !== 'Unknown' ? `${result.identified.brand} ` : ''}
                      {result.identified.model !== 'Unknown' ? result.identified.model : result.identified.category}
                    </div>
                    <div className="text-[11.5px] text-white/40 mt-0.5">
                      {result.identified.category} · searched: "{result.identified.keywords}"
                    </div>
                  </div>
                  <span className={`text-[10px] font-medium capitalize ${CONFIDENCE_COLOUR[result.identified.confidence] ?? 'text-white/40'}`}>
                    {result.identified.confidence} confidence
                  </span>
                </div>
              )}

              {/* Warning */}
              {result.warning && (
                <div className="card p-4 flex items-start gap-2 border-amber-500/20">
                  <AlertCircle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-[12px] text-amber-300">{result.warning}</p>
                </div>
              )}

              {/* Sold data summary */}
              {result.soldData && result.soldData.count > 0 && (
                <>
                  <div className="card p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-[10px] font-semibold text-white/30 uppercase tracking-wider">
                        eBay UK — last {result.soldData.daysBack} days
                      </div>
                      <div className="text-[11px] text-white/40">
                        {result.soldData.count} sold
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2 mb-4">
                      <PriceCard label="Lowest" pence={result.soldData.lowestPence} icon={<TrendingDown size={12} />} colour="text-danger" />
                      <PriceCard label="Median" pence={result.soldData.medianPence} icon={<Minus size={12} />} colour="text-secondary-400" highlight />
                      <PriceCard label="Highest" pence={result.soldData.highestPence} icon={<TrendingUp size={12} />} colour="text-primary-300" />
                    </div>

                    <div className="text-[11px] text-white/30 text-center">
                      Average: <span className="text-white/50">{p(result.soldData.averagePence)}</span>
                    </div>
                  </div>

                  {/* Profit calculator results */}
                  {result.profit && (
                    <div className="card p-5">
                      <div className="text-[10px] font-semibold text-white/30 uppercase tracking-wider mb-3">
                        Estimated profit at median price ({p(result.profit.suggestedSellPence)})
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <PlatformProfit
                          platform="eBay"
                          colour="#e53935"
                          side={result.profit.ebay}
                          shippingPence={shippingPence}
                          feeNote={result.fees?.ebay.notes}
                          feeSource={result.fees?.ebay.source}
                        />
                        <PlatformProfit
                          platform="Vinted"
                          colour="#09b864"
                          side={result.profit.vinted}
                          shippingPence={shippingPence}
                          feeNote={result.fees?.vinted.notes}
                          feeSource={result.fees?.vinted.source}
                        />
                      </div>
                      {!buyPrice && (
                        <p className="text-[10.5px] text-white/25 mt-3 text-center">
                          Enter your buy price above to see profit
                        </p>
                      )}
                      {result.fees && (
                        <p className="text-[9.5px] text-white/20 mt-3 text-center">
                          Fees verified via live search · {result.fees.asOf || new Date(result.fees.fetchedAt).toLocaleDateString('en-GB')}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Recent sales */}
                  <div className="card overflow-hidden">
                    <div className="px-4 py-3 border-b border-white/[0.05]">
                      <h3 className="text-[12px] font-semibold">Recent sold listings</h3>
                    </div>
                    <div className="divide-y divide-white/[0.04]">
                      {result.soldData.recentSales.map((sale, i) => (
                        <div key={i} className="flex items-center justify-between px-4 py-2.5 gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="text-[12px] text-white/70 truncate">{sale.title}</div>
                            <div className="text-[10.5px] text-white/30 mt-0.5">
                              {sale.condition} · {formatDate(sale.soldDate)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-[13px] font-semibold text-secondary-400">
                              {p(sale.pricePence)}
                            </span>
                            {sale.url && (
                              <a
                                href={sale.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-white/20 hover:text-white/50 transition-colors"
                              >
                                <ExternalLink size={12} />
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Create listing CTA */}
                  <button
                    onClick={createListing}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                  >
                    <Plus size={14} /> Create listing from this item
                  </button>
                </>
              )}

              {/* No results */}
              {result.soldData && result.soldData.count === 0 && (
                <div className="card p-8 text-center">
                  <ShoppingCart size={28} className="text-white/10 mx-auto mb-3" />
                  <p className="text-sm text-white/40">No sold listings found on eBay UK</p>
                  <p className="text-[11px] text-white/25 mt-1">This item may be rare or hard to resell</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PriceCard({
  label, pence, icon, colour, highlight,
}: {
  label: string; pence: number; icon: React.ReactNode; colour: string; highlight?: boolean;
}) {
  return (
    <div className={`rounded-xl p-3 border text-center ${highlight ? 'border-secondary-400/25 bg-secondary-400/[0.05]' : 'border-white/[0.06] bg-white/[0.02]'}`}>
      <div className={`flex items-center justify-center gap-1 text-[10px] mb-1 ${colour}`}>
        {icon} {label}
      </div>
      <div className={`text-[18px] font-semibold ${highlight ? 'text-secondary-400' : 'text-white/70'}`}>
        {p(pence)}
      </div>
    </div>
  );
}

function PlatformProfit({
  platform, colour, side, shippingPence, feeNote, feeSource,
}: {
  platform: string; colour: string; side: ProfitSide; shippingPence: number;
  feeNote?: string; feeSource?: string;
}) {
  const hasProfit = side.profitPence !== null;
  const profitable = hasProfit && side.profitPence! > 0;

  return (
    <div className="rounded-xl border border-white/[0.06] p-3 space-y-1.5">
      <div className="flex items-center gap-1.5 mb-2">
        <div className="w-2 h-2 rounded-full" style={{ background: colour }} />
        <span className="text-[12px] font-medium">{platform}</span>
        {hasProfit && (
          profitable
            ? <CheckCircle2 size={12} className="text-secondary-400 ml-auto" />
            : <XCircle size={12} className="text-danger ml-auto" />
        )}
      </div>
      <Row label="Platform fee" value={`-${p(side.feePence)}`} muted />
      <Row label="Shipping" value={`-${p(shippingPence)}`} muted />
      <div className="border-t border-white/[0.06] pt-1.5 mt-1.5">
        <Row
          label={hasProfit ? 'Net profit' : 'Net after fees'}
          value={hasProfit ? p(side.profitPence!) : p(side.netAfterFeesPence)}
          highlight={profitable}
          danger={hasProfit && !profitable}
        />
        {side.marginPct !== null && (
          <Row label="Margin" value={`${side.marginPct}%`} muted />
        )}
      </div>
      {feeNote && (
        <div className="mt-2 pt-2 border-t border-white/[0.05]">
          <p className="text-[9.5px] text-white/25 leading-relaxed">{feeNote}</p>
          {feeSource && (
            <a href={feeSource} target="_blank" rel="noopener noreferrer"
              className="text-[9px] text-white/20 hover:text-primary-300 flex items-center gap-0.5 mt-0.5 transition-colors">
              official fees page <ExternalLink size={8} />
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, muted, highlight, danger }: {
  label: string; value: string; muted?: boolean; highlight?: boolean; danger?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[10.5px] text-white/30">{label}</span>
      <span className={`text-[11px] font-medium ${highlight ? 'text-secondary-400' : danger ? 'text-danger' : muted ? 'text-white/40' : 'text-white/70'}`}>
        {value}
      </span>
    </div>
  );
}

function FeeRow({ platform, colour, fee }: {
  platform: string;
  colour: string;
  fee: PlatformFee;
}) {
  const feeLabel = fee.fvfPercent === 0 && fee.fixedFeePence === 0
    ? 'No seller fees'
    : fee.fvfPercent > 0 && fee.fixedFeePence > 0
    ? `${fee.fvfPercent}% + ${p(fee.fixedFeePence)}`
    : fee.fvfPercent > 0
    ? `${fee.fvfPercent}%`
    : `${p(fee.fixedFeePence)} fixed`;

  return (
    <div className="flex items-start justify-between gap-2">
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <div className="w-1.5 h-1.5 rounded-full mt-0.5 flex-shrink-0" style={{ background: colour }} />
        <span className="text-[10.5px] text-white/40">{platform}</span>
      </div>
      <div className="text-right">
        <span className="text-[10.5px] text-white/60 font-medium">{feeLabel}</span>
        {fee.source && (
          <a
            href={fee.source}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[9.5px] text-white/20 hover:text-primary-300 flex items-center justify-end gap-0.5 mt-0.5 transition-colors"
          >
            source <ExternalLink size={9} />
          </a>
        )}
      </div>
    </div>
  );
}
